<template>
  <section class="user-select-none">
    <b-button v-b-toggle="'leash-size-select-help'" variant="link" class="float-right mb-0 text-primary p-0">
      <h3>
        <u>{{ $t('otherProductSelect.sizingHelp') }}</u>
      </h3>
    </b-button>
    <h2 class="mb-0">{{ $t('leashSizeSelect.title') }}</h2>
    <h3 class="d-none d-xl-block text-dark">{{ $t('leashSizeSelect.approxSize') }}</h3>
    <b-collapse id="leash-size-select-help" class="mt-3 px-4 py-3">
      <div class="arrow" />
      <div class="position-relative float-right">
        <h3 v-b-toggle="'leash-size-select-help'" class="text-primary" href="javascript:void(0)">
          <u>{{ $t('dp.navbar.toggle.close') }}</u>
        </h3>
      </div>
      <div class="text-center" style="margin-left: 49px">
        <h2 class="font-weight-bold text-dark">{{ $t('leashSizeSelect.sizingHelp') }}</h2>
      </div>
      <h3 class="text-center text-dark">
        {{ $t('leashSizeSelect.text') }}
      </h3>
    </b-collapse>
    <b-row class="my-3">
      <b-col v-for="[sizeKey, sizeDetails] in Object.entries(sizes)" :key="sizeKey" class="text-center">
        <b-button
          :id="`leash-size-select-${sizeKey}`"
          :ref="sizeKey"
          class="size-option pt-2"
          block
          :class="{ 'no-stock': isOutOfStock(sizeDetails), selected: size === sizeKey }"
          :variant="!isOutOfStock(sizeDetails) ? 'outline-primary' : 'outline'"
          :disabled="isOutOfStock(sizeDetails)"
          @focus="setFocus(sizeKey)"
          @focusout="unsetFocus"
          @click="isOutOfStock(sizeDetails) ? null : setSize(sizeKey)"
        >
          <p class="h3 mb-0" :class="isOutOfStock(sizeDetails) ? 'text-light' : 'text-primary'">
            <strong>{{ sizeDetails.length || sizeKeyMap[sizeKey] || 'N/A' }}</strong>
            &nbsp;<span v-if="sizeDetails.width" class="h4 text-light">({{ sizeDetails.width }})</span>
          </p>
          <svg v-show="isOutOfStock(sizeDetails)" aria-hidden="true"><line x1="0" y1="100%" x2="100%" y2="0" /></svg>
        </b-button>
        <div v-if="isOutOfStock(sizeDetails)" class="text-danger copy" v-text="$t('product.outOfStock')" />
        <section v-if="sizeDetails && sizeDetails.title">
          <b-popover
            v-if="!isOutOfStock(sizeDetails)"
            :target="`leash-size-select-${sizeKey}`"
            triggers="hover"
            placement="bottom"
            custom-class="size-pop-over user-select-none"
          >
            <div>
              <h2 v-if="sizeDetails.title" class="text-center text-dark font-weight-bold">{{ sizeDetails.title }}</h2>
              <h4 class="text-center text-dark mt-n1">
                <span v-if="sizeDetails.length"><strong>{{ $t('dp.generic.length') }}:</strong>&nbsp;{{ sizeDetails.length }}&nbsp;<strong>|</strong>&nbsp;</span>
                <span v-if="sizeDetails.width"><strong>{{ $t('dp.generic.width') }}:</strong>&nbsp;{{ sizeDetails.width }}</span>
              </h4>
              <b-row class="mt-3">
                <b-col class="pr-0" :cols="5">
                  <b-img v-if="sizeDetails.image" :src="getImage(sizeDetails.image.original)" alt="" rounded fluid />
                </b-col>
                <b-col class="pl-2">
                  <h4 v-if="sizeDetails.description" class="text-dark">
                    {{ sizeDetails.description }}
                  </h4>
                </b-col>
              </b-row>
            </div>
          </b-popover>
        </section>
      </b-col>
    </b-row>
    <ValidationProvider v-slot="{ errors }" vid="p-size-select" :rules="{ required: { allowFalse: false } }" slim>
      <input id="product-style-color" v-model="value" type="text" class="d-none" hidden />
      <span v-if="errors && errors[0]" class="text-danger">{{ $t('leashSizeSelect.error') }}</span>
    </ValidationProvider>
  </section>
</template>

<script>
import { ProductMixins } from '@/mixins/components/product';
import { KeyboardNavigationMixins } from '@/mixins/keyboardNavigation';

export default {
  name: 'LeashSizeSelect',
  mixins: [ProductMixins, KeyboardNavigationMixins],
  computed: {
    sizeKeyMap() {
      return { S: '4 ft', L: '6 ft' };
    },
    navigableOptions() {
      let options = [];
      if (!this.disableSmall) {
        options.push('S');
      }
      if (!this.disableLarge) {
        options.push('L');
      }
      return options;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
.size-option {
  border-radius: 10px;
  border: 2px solid $dp-gray-4;
  height: auto;
  &.selected {
    border: 2px solid $dp-blue;
    background-color: rgba(204, 236, 249, 0.5);
  }
  &:hover,
  &:active,
  &:focus {
    &:not(.disabled) {
      border: 2px solid $dp-blue;
      box-shadow: 0 0 0 0.2rem $dp-light-blue;
      background-color: rgba(204, 236, 249, 0.5) !important;
    }
  }
}
#leash-size-select-help {
  position: relative;
  border-radius: 6px;
  background-color: $dp-gray-1;
  border: 2px solid $dp-gray-3;
  &:not(.collapsing) .arrow {
    &:after,
    &:before {
      left: 94%;
      content: '';
      bottom: 100%;
      position: absolute;
      pointer-events: none;
      border: solid transparent;
    }
    &:after {
      border-color: rgba(247, 247, 247, 0);
      border-bottom-color: $dp-gray-1;
      border-width: 12px;
      margin-left: -12px;
    }
    &:before {
      border-color: rgba(220, 220, 220, 0);
      border-bottom-color: $dp-gray-3;
      border-width: 15px;
      margin-left: -15px;
    }
  }
}
.size-out-of-stock-pop-over {
  &.bs-popover-top {
    top: -10px !important;
  }
  &.bs-popover-bottom {
    top: 10px !important;
  }
  &.bs-popover-right {
    left: 10px !important;
  }
  &.bs-popover-left {
    left: -10px !important;
  }
  max-width: 600px;
}
.size-pop-over {
  &.bs-popover-top {
    top: -10px !important;
  }
  &.bs-popover-bottom {
    top: 10px !important;
  }
  &.bs-popover-right {
    left: 10px !important;
  }
  &.bs-popover-left {
    left: -10px !important;
  }
  max-width: 600px;
  background-color: $dp-gray-1;
}
</style>
